// Copyright (c) Ping. All rights reserved.
//
// Single source of truth for "wipe every byte of locally-owned session
// state". Used by:
//   * Settings → Sign out this device
//   * Settings → Sign out everywhere (after the BE revoke roundtrip)
//   * Bootstrap's identity-corruption / SDK-storage-corruption recovery
//     paths
//   * App.tsx silent re-auth failure path
//
// Skipping any of these surfaces is a footgun: a stale Dexie chat row,
// a leftover MLS IDB blob, or a non-cleared token can resurface as a
// "ghost chat" / phantom 401 / "your account is bound to another
// device" loop on the next mount.
//
// All steps are wrapped individually so one failing layer doesn't
// abort the rest of the cleanup. The function never throws — caller
// is expected to follow up with `window.location.reload()` or a
// router push to the welcome screen.

import { tearDownSession } from '@ping/ui';
import { identityManager } from '@ping/ui/src/platform/identity.web';
import { PingStorageWeb } from '@ping/ui/src/storage-mls/PingStorage.web';

import { log } from '@core/log/log';
import { clearAccessToken } from '@core/network/interceptor';
import { clearIdentity } from '@core/persistence/identity';
import { clearRefreshToken } from '@core/persistence/tokens';
import { clearAllData } from '@core/storage/idb';

import { stopEphemeralSession } from './ephemeralSession';
import { resetWebSessionRegistries } from './sessionBootstrap';

export interface ClearLocalStateOptions {
  /**
   * Skip the Zustand session-store teardown when the caller already
   * holds a stronger reference (e.g. the auth-flow failure path,
   * which is invoked before the store ever transitioned to
   * `authenticated`). Default `false`.
   */
  readonly skipSessionTeardown?: boolean;
  /**
   * Free-form tag included in the structured log so post-hoc
   * grepping can attribute a wipe to its trigger ("signOut.current",
   * "signOut.all", "bootstrap.storageCorruption", etc.).
   */
  readonly reason: string;
  /**
   * Also delete the device-layer crypto keys
   * (`__device_signing_key`, `__user_identity_key`, the refresh-token
   * wrap key). Default `false`.
   *
   * IMPORTANT default rationale: the device signing key IS this
   * device's identity to the BE — `device_id = SHA-256(pubkey)` is
   * what the BE registered against the user's account at /v1/auth/verify.
   * Wiping it on a normal sign-out makes the next sign-in present a
   * *different* pubkey for the same phone/email and the BE responds
   * with `identity_mismatch` (403) → the user sees "Account already
   * on another device" even though they're the legitimate owner.
   *
   * The catch paths that DO want to nuke the key — identity-
   * corruption recovery, device-id-mismatch recovery — pass
   * `forgetDevice: true` so the next bootstrap mints a brand-new
   * device.
   */
  readonly forgetDevice?: boolean;
  /**
   * Wipe profile, controlling which residue we tolerate:
   *
   *   - 'logout' (explicit user action): zero residue. Forces
   *     `forgetDevice: true` and clears `localStorage` keys prefixed
   *     `ping.` on top of the standard wipe. The next sign-in on
   *     this browser starts a brand-new device record.
   *
   *   - 'arrival' (defensive wipe on welcome-screen mount): zero
   *     residue from a prior user. Also forces `forgetDevice: true`
   *     so a different person opening this browser later cannot
   *     accidentally see the previous user's device keys; the
   *     trade-off (one extra "Account already on another device"
   *     prompt for the legitimate user) is acceptable because the
   *     arrival path is reached only when nothing else is signed in.
   *
   *   - 'session-failure' (bootstrap / silent-reauth catch): the
   *     legacy behaviour — preserves device crypto keys so the
   *     user's next sign-in re-uses the same pubkey and the BE
   *     recognises the device.
   *
   *   - 'logout-preserve' (PERFECT-resume sign-out): clears the SESSION
   *     (access + refresh tokens → the user sees the login screen and
   *     must OTP back in) but PRESERVES this device's MLS identity, group
   *     state, message history, and sync cursors. Re-signing in on the
   *     SAME browser resumes the EXACT same MLS leaf — all conversations
   *     + messages present instantly, live delivery, no duplicate 1:1s,
   *     and any messages a peer sent while signed out are fetched from the
   *     preserved cursors and decrypted with the surviving leaf. The BE
   *     revoke on logout is undone by `RegisterDeviceOnLogin` reactivating
   *     the same-pubkey device on the next verify (no re-pin, no
   *     `identity_mismatch`). Trade-off: a signed-out device retains
   *     decryptable data on disk — the caller MUST offer an explicit
   *     "Erase this device" (mode 'logout') for the zero-residue case, and
   *     "Sign out everywhere" stays a full wipe. A DIFFERENT browser has
   *     no local identity, so it still mints a fresh leaf (link / recover /
   *     peer-readmit) — this mode only helps the same-browser round-trip.
   *
   * Optional for backwards compatibility; omitting it falls back to
   * `forgetDevice ?? false` and behaves exactly like the pre-mode
   * version of this function.
   */
  readonly mode?: 'logout' | 'arrival' | 'session-failure' | 'logout-preserve';
  /**
   * Preserve the persisted refresh token + identity row across this
   * teardown. Set ONLY on transient failure paths (silent-reauth /
   * bootstrap hiccup, a flaky-refresh 401 that wasn't a genuine
   * invalid-token) where the credentials are still valid and the next
   * reload should silently re-authenticate instead of logging the user
   * out.
   *
   * This is the linchpin of the "stay logged in for ≥1 year" contract:
   * a recoverable blip on reload must NOT destroy the refresh token.
   * Mutually exclusive with `forgetDevice`/logout/arrival — if those
   * force a device wipe, credentials go with it.
   */
  readonly preserveCredentials?: boolean;
}

// Note: previously this file kept a `DEVICE_IDENTITY_KEYS` allow-list
// (`__device_signing_key`, `__user_identity_key`,
// `__webcrypto.refresh_wrapper_key`) used by the partial-wipe path
// to skip preserved keys when emptying `settings_kv`. With the
// post-bugfix gating (transient wipes preserve `settings_kv`
// entirely; only forgetDevice clears it outright), the allow-list
// is no longer referenced. Kept as a comment so anyone editing the
// gating later remembers which keys MUST survive same-user
// re-sign-in: a wipe of those device keys causes the next verify to
// present a new user_identity_pubkey and the BE 403s with
// identity_mismatch.

export async function clearAllLocalSessionState(
  options: ClearLocalStateOptions,
): Promise<void> {
  const { reason, skipSessionTeardown, mode } = options;
  // `mode` is the new high-level switch; `forgetDevice` is the
  // legacy flag callers still pass. Resolve to a single effective
  // value: logout + arrival both wipe device keys (zero residue);
  // session-failure keeps them.
  const forgetDevice =
    mode === 'logout' || mode === 'arrival'
      ? true
      : mode === 'session-failure' || mode === 'logout-preserve'
        ? false
        : (options.forgetDevice ?? false);
  log.warn({
    event: 'session.localWipe.begin',
    reason,
    forgetDevice,
    ...(mode !== undefined ? { mode } : {}),
  });

  // 1. Tear down the SDK client + session store. This closes the
  //    Web Worker that owns the WASM client, otherwise it would
  //    keep its in-memory state alive until tab close.
  if (!skipSessionTeardown) {
    stopEphemeralSession();
    try {
      await tearDownSession();
    } catch (err) {
      log.warn({
        event: 'session.localWipe.teardownFailed',
        message: (err as Error).message,
      });
    }
  }

  // 1b. Reset the module-scoped session singletons (active PingTransport +
  //     current-account registry). Always — even on session-failure mode —
  //     because a wipe always means "this session is over": the next bootstrap
  //     constructs a fresh transport, and leaving the old one live risks a
  //     stale WS frame or a stale `isSelf` attribution leaking across an
  //     account switch. Disposes the transport (force-closes its socket).
  try {
    resetWebSessionRegistries();
  } catch (err) {
    log.warn({
      event: 'session.localWipe.registriesFailed',
      message: (err as Error).message,
    });
  }

  // 2. Wipe the MLS storage IDB (`ping-mls-storage`) — ONLY when
  //    `forgetDevice` is true (i.e. explicit logout, arrival on a
  //    shared browser, or a hard storage-corruption recovery).
  //
  //    The original comment here claimed wiping was harmless because
  //    "the wrap key lived only in memory"; that's wrong — the wrap
  //    key is persisted in the SAME IDB under `__wrap-key`, so the
  //    same identity CAN re-decrypt on next bootstrap. Wiping
  //    unconditionally destroys conversation history every time a
  //    transient failure (silent-reauth fail, refresh interceptor
  //    401, bootstrap retry) routes through here — the user reloads,
  //    bootstrap succeeds, but every chat shows up empty / orphans
  //    because the SDK's listConversations() comes back blank.
  //
  //    Gated on forgetDevice instead of an unconditional wipe so
  //    same-user re-sign-in retains its MLS state.
  if (forgetDevice) {
    try {
      await new PingStorageWeb().forgetSession();
    } catch (err) {
      log.warn({
        event: 'session.localWipe.mlsStorageFailed',
        message: (err as Error).message,
      });
    }
  }

  // 3. Forget the identity wrap key + the wrapped identity bytes —
  //    also gated on `forgetDevice`. Same reasoning as step 2:
  //    when we're keeping the device record (forgetDevice=false)
  //    the identity must survive too so the next sign-in unwraps
  //    the SAME bytes and the SDK adopts the same identity.
  if (forgetDevice) {
    try {
      await identityManager.forgetIdentity();
    } catch (err) {
      log.warn({
        event: 'session.localWipe.identityFailed',
        message: (err as Error).message,
      });
    }
  }

  // 4. Always drop the stale in-memory access token (it's worthless
  //    after a teardown regardless).
  //
  //    The persisted refresh token is normally dropped (a wipe means
  //    "current session is done"), EXCEPT on `preserveCredentials`
  //    transient paths — THE "stay logged in" gate. A recoverable blip
  //    on reload (silent-reauth hiccup, bootstrap deadline, a
  //    flaky-refresh 401 that wasn't a genuine invalid-token) must NOT
  //    destroy the refresh token, or the next reload has nothing to
  //    silently re-auth with and the user is permanently logged out.
  //    Preserving it means the next reload mints a fresh access token
  //    off the surviving refresh token (target: ≥1 year, bounded only
  //    by the BE's refresh-token TTL). `forgetDevice` always wins —
  //    an explicit logout/arrival wipe takes the credentials with it.
  const preserveCredentials = options.preserveCredentials === true && !forgetDevice;
  try {
    clearAccessToken();
  } catch {
    /* best-effort */
  }
  // The persisted identity ROW (display name / accountId hint) is only
  // dropped when forgetDevice is true — same-user re-sign-in benefits
  // from the cached identity to pre-fill the welcome screen, and the
  // transient path needs it to silently re-auth.
  await Promise.all([
    preserveCredentials
      ? Promise.resolve()
      : clearRefreshToken().catch((err) => {
          log.warn({
            event: 'session.localWipe.refreshTokenFailed',
            message: (err as Error).message,
          });
        }),
    forgetDevice
      ? clearIdentity().catch((err) => {
          log.warn({
            event: 'session.localWipe.identityRowFailed',
            message: (err as Error).message,
          });
        })
      : Promise.resolve(),
  ]);

  // 5. Clear the IDB metadata + sync_cursors stores (`ping-db`).
  //    Cursors are per-conversation; clearing them on every
  //    transient wipe forces a full re-sync on next bootstrap
  //    (every conv re-downloads from `since 0`). Gate on
  //    forgetDevice so same-user reload reuses its cursor state.
  if (forgetDevice) {
    try {
      await clearAllData();
    } catch (err) {
      log.warn({
        event: 'session.localWipe.idbFailed',
        message: (err as Error).message,
      });
    }
  }

  // 6. Clear the Dexie chat tables (`ping-persistence`). Each
  //    `.clear()` is a separate transaction; running them in parallel
  //    is safe because they touch disjoint stores.
  //
  //    `settings_kv` holds two distinct flavours of data:
  //    user preferences (theme / accent / etc.) AND the device-layer
  //    crypto keys (signing key, user identity key, refresh-wrap key).
  //    For a *normal* sign-out we wipe everything in `settings_kv`
  //    EXCEPT the device crypto keys (see `DEVICE_IDENTITY_KEYS`),
  //    so the next sign-in to the same account re-uses the same
  //    pubkey and the BE recognises this device — no spurious
  //    `identity_mismatch`. When `forgetDevice` is true we clear the
  //    table outright (true reset).
  // Chat-table wipes are gated on `forgetDevice` for the same
  // reason as steps 2 and 5: a transient goUnauthenticated (e.g.
  // a 401 from a flaky refresh) shouldn't destroy the user's
  // message history. The same user re-signing in expects their
  // chats to still be there.
  //
  // The `settings_kv` table is more nuanced — it holds both user
  // preferences AND the device crypto keys + the MLS conversation
  // pointers (`mls.conv.*` / `mls.invited.*`). For the non-wipe
  // path we now leave it untouched entirely (preserving everything
  // so re-sign-in is seamless). For forgetDevice we clear it
  // outright as before.
  if (forgetDevice) {
    try {
      const { db } = await import('@core/persistence/db');
      await Promise.all([
        db.table('messages').clear(),
        db.table('conversations').clear(),
        db.table('drafts').clear(),
        db.table('attachments_meta').clear(),
        db.table('settings_kv').clear(),
        db.table('call_history').clear(),
        db.table('remote_call_history').clear(),
      ]);
    } catch (err) {
      log.warn({
        event: 'session.localWipe.dexieFailed',
        message: (err as Error).message,
      });
    }
  }

  // 7. SessionStorage holds the linking-priv (for the QR pairing
  //    flow) and a couple of devtools handles. Wipe under our prefix.
  try {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem('ping.linkingPriv.b64u');
    }
  } catch {
    /* best-effort */
  }

  // 8. Aggressive wipe for logout + arrival modes: every localStorage
  //    key prefixed `ping.` and (best-effort) `ping-mls-storage` IDB
  //    if any direct write slipped past PingStorageWeb.forgetSession.
  //    Skipped for session-failure paths to preserve the device-key
  //    survival contract there.
  if (mode === 'logout' || mode === 'arrival') {
    try {
      if (typeof localStorage !== 'undefined') {
        const drop: string[] = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key !== null && key.startsWith('ping.')) {
            drop.push(key);
          }
        }
        for (const key of drop) {
          localStorage.removeItem(key);
        }
      }
    } catch {
      /* best-effort */
    }
    try {
      if (typeof indexedDB !== 'undefined' && 'deleteDatabase' in indexedDB) {
        // Fire-and-forget; PingStorageWeb.forgetSession already
        // ran above, this is a backstop for any orphan handle.
        indexedDB.deleteDatabase('ping-mls-storage');
      }
    } catch {
      /* best-effort */
    }
  }

  log.info({ event: 'session.localWipe.complete', reason });
}
